import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Container,
  Row,
  Col,
  OverlayTrigger,
  Tooltip,
  Button,
} from "react-bootstrap";
import { FaEye } from "react-icons/fa";
import { IoIosArrowUp } from "react-icons/io";
import {
  getSubscriptions,
} from "../../actions/subscriptionAction";
import resetBtn from "../../assets/images/reset-arrow.svg";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import FullPageLoader from "../../components/FullPageLoader/FullPageLoader";
import { toast } from "react-toastify";
import DataTable, { createTheme } from "react-data-table-component";
import InitializeLocalStorage from "../../components/PaginationHandle";

createTheme(
  "solarized",
  {
    text: {
      primary: "#000",
      secondary: "#000",
    },
    background: {
      default: "#fff",
    },
    context: {
      background: "#cb4b16",
      text: "#FFFFFF",
    },
    divider: {
      default: "#DFE0EB",
    },
    action: {
      button: "rgba(0,0,0,.54)",
      hover: "rgba(0,0,0,.08)",
      disabled: "rgba(0,0,0,.12)",
    },
  },
  "dark"
);

const SubscriptionList = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loader, setLoader] = useState(false);
  const { admin } = useSelector((state) => state.admin);
  const currentUserRole = admin?.role;
  const [filterText, setFilterText] = useState("");
  const [selectedPackage, setSelectedPackage] = useState("");
  const [selectedRecurring, setSelectedRecurring] = useState("");
  const [filteredItems, setFilteredItems] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const location = useLocation();
  const { pathname } = location;

  InitializeLocalStorage(pathname, localStorage.getItem("currentURL"));

  const handlePageChange = (page) => {
    getAllSubscriptions(page);
    setCurrentPage(page);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    getAllSubscriptions(page, newPerPage);
    setPerPage(newPerPage);
  };

  const getAllSubscriptions = async (page = 1, limit = perPage) => {
    setLoader(true);
    try {
      const params = {
        page,
        limit,
      };
      
      if (selectedPackage) params.package = selectedPackage;
      if (selectedRecurring !== "") params.isRecurring = selectedRecurring;
      if (filterText) params.search = filterText;

      let res = await getSubscriptions(params);
      if (res?.data?.success) {
        const data = res.data.data || [];
        setTotalRows(res.data.pagination?.total || data.length);
        setSubscriptions(data);
        setFilteredItems(data);
      }
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
      toast.error("Error fetching subscriptions");
    }
    setLoader(false);
  };

  useEffect(() => {
    getAllSubscriptions();
  }, []);

  useEffect(() => {
    getAllSubscriptions(1);
  }, [selectedPackage, selectedRecurring, filterText]);

  const convertDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const options = {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: true,
    };
    return date.toLocaleDateString(undefined, options).replace(/\//g, "-");
  };

  const columns = [
    {
      name: "#",
      selector: (row, index) => {
        const counter = ((currentPage - 1) * perPage) + index + 1;
        return counter;
      },
      sortable: false,
      minWidth: "50px",
    },
    {
      name: "Order ID",
      selector: (row) => row.orderId || "N/A",
      sortable: false,
      minWidth: "100px",
    },
    {
      name: "User",
      selector: (row) => {
        const user = row.userId;
        if (!user) return "N/A";
        return `${user.firstName || ""} ${user.lastName || ""}`.trim() || "N/A";
      },
      sortable: false,
      minWidth: "150px",
    },
    {
      name: "Email",
      selector: (row) => row.userId?.email || "N/A",
      sortable: false,
      minWidth: "200px",
    },
    {
      name: "Phone",
      selector: (row) => row.userId?.phone || "N/A",
      sortable: false,
      minWidth: "150px",
    },
    {
      name: "Package",
      selector: (row) => row.package?.charAt(0).toUpperCase() + row.package?.slice(1) || "N/A",
      sortable: false,
      minWidth: "120px",
    },
    {
      name: "Price",
      selector: (row) => `$${row.price?.toFixed(2) || "0.00"}`,
      sortable: false,
      minWidth: "100px",
    },
   
    {
      name: "Recurring",
      selector: (row) => (
        <>
          {row.isRecurring ? (
            <label className="label label-success m-0">Yes</label>
          ) : (
            <label className="label label-secondary m-0">No</label>
          )}
        </>
      ),
      sortable: false,
      minWidth: "100px",
    },
   
    {
      name: "Paid Date",
      selector: (row) => convertDate(row.paidDate),
      sortable: false,
      minWidth: "180px",
    },
    {
      name: "Created At",
      selector: (row) => convertDate(row.createdAt),
      sortable: false,
      minWidth: "180px",
    },
    {
      name: "Payment Status",
      selector: (row) => (
        <>
          {row.paymentStatus === "paid" ? (
            <label className="label label-success m-0">Paid</label>
          ) : (
            <label className="label label-danger m-0">{row.paymentStatus || "Unpaid"}</label>
          )}
        </>
      ),
      sortable: false,
      minWidth: "120px",
    },
    {
      name: "Actions",
      selector: (row) => (
        <div className="td-actions table-actions-btns">
          <ul className="list-unstyled mb-0 d-flex justify-content-center">
            {currentUserRole?.viewSubscriptionList && (
              <li className="d-inline-block align-top">
                <OverlayTrigger
                  overlay={<Tooltip id="tooltip-view">View Details</Tooltip>}
                  placement="left"
                >
                  <Link
                    to={`/subscription/view/${row._id}`}
                    type="button"
                    className="btn-link btn-icon btn btn-primary"
                  >
                    <FaEye />
                  </Link>
                </OverlayTrigger>
              </li>
            )}
          </ul>
        </div>
      ),
      sortable: false,
    },
  ];

  const filterDatatable = (text) => {
    setFilterText(text);
  };

  const resetFilter = () => {
    setFilterText("");
    setSelectedPackage("");
    setSelectedRecurring("");
    getAllSubscriptions(1);
  };

  const toggleFilterAccordion = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  return (
    <>
      {loader ? (
        <FullPageLoader />
      ) : (
        <div className="pt-2 pt-md-3">
          <Container fluid>
            <Row>
              <Col md="12">
                {/* Filter Card */}
                <Card className="filter-card card">
                  <Card.Header
                    style={{ cursor: "pointer" }}
                    onClick={toggleFilterAccordion}
                  >
                    <div className="d-flex align-items-center justify-content-between table-head">
                      <Card.Title className="text-white" as="h4">
                        Filter
                      </Card.Title>
                      <div
                        style={{
                          transform: isFilterOpen ? "rotate(0deg)" : "rotate(180deg)",
                          transition: "transform 0.3s ease",
                        }}
                      >
                        <IoIosArrowUp />
                      </div>
                    </div>
                  </Card.Header>
                  {isFilterOpen && (
                    <Card.Body>
                      <Row>
                        <Col xl={4} sm={6} className="search-wrap">
                          <Form.Group>
                            <Form.Label className="d-block mb-2 text-white">
                              Search...
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="search"
                              placeholder="Search by name, email, order ID, or ARB ID"
                              value={filterText}
                              onChange={(e) => filterDatatable(e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                        <Col xl={4} sm={6} className="search-wrap">
                          <Form.Group>
                            <Form.Label className="d-block mb-2 text-white">
                              Filter by Package...
                            </Form.Label>
                            <Form.Control
                              as="select"
                              value={selectedPackage}
                              onChange={(e) => setSelectedPackage(e.target.value)}
                            >
                              <option value="">All Packages</option>
                              <option value="quarterly">Quarterly</option>
                              <option value="monthly">Monthly</option>
                              <option value="yearly">Yearly</option>
                            </Form.Control>
                          </Form.Group>
                        </Col>
                        <Col xl={4} sm={6} className="search-wrap">
                          <Form.Group>
                            <Form.Label className="d-block mb-2 text-white">
                              Filter by Recurring...
                            </Form.Label>
                            <Form.Control
                              as="select"
                              value={selectedRecurring}
                              onChange={(e) => setSelectedRecurring(e.target.value)}
                            >
                              <option value="">All</option>
                              <option value="true">Recurring</option>
                              <option value="false">One-time</option>
                            </Form.Control>
                          </Form.Group>
                        </Col>
                        <Col xl={4} md={6}>
                          <Form.Group>
                            <label className="d-none d-sm-block mb-2 form-label">
                              &nbsp;
                            </label>
                            <div className="d-flex filter-btns-holder">
                              <button
                                type="button"
                                className="outline-button filter-reset-btn"
                                onClick={() => resetFilter()}
                              >
                                <img
                                  src={resetBtn}
                                  className="img-fluid mr-3"
                                  alt="reset"
                                />
                                Reset
                              </button>
                            </div>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  )}
                </Card>

                {/* DataTable Header */}
                <div className="datatable-title card d-flex flex-row justify-content-between align-items-center mb-0">
                  <h4 className="card-title">Subscriptions</h4>
                </div>

                {/* DataTable */}
                <div className="custom-datatable-pagination">
                  <DataTable
                    columns={columns}
                    data={filteredItems}
                    theme="solarized"
                    pagination
                    className="react-datatable"
                    progressPending={loader}
                    paginationServer={true}
                    paginationTotalRows={totalRows}
                    paginationDefaultPage={currentPage}
                    paginationPerPage={perPage}
                    onChangeRowsPerPage={handlePerRowsChange}
                    onChangePage={handlePageChange}
                  />
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      )}
    </>
  );
};

export default SubscriptionList;

